import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLock, FaExclamationTriangle } from 'react-icons/fa';
import PlanCard from '../PlanCard/PlanCard';
import { GoldStar, SilverStar } from '../PlanCard/PlanCard';
import './TrialExpiredOverlay.scss';

/**
 * TrialExpiredOverlay - Full-screen blocking overlay when trial has expired
 * Forces user to choose a plan before they can continue using the platform
 */
const TrialExpiredOverlay = ({
  isVisible,
  daysExpired = 0,
  hasPaidPlan = false,
  planTier = 'free',
}) => {
  const navigate = useNavigate();

  if (!isVisible) {
    return null;
  }

  const normalizedPlan = String(planTier || '').trim().toLowerCase();
  const planName = normalizedPlan
    ? normalizedPlan.charAt(0).toUpperCase() + normalizedPlan.slice(1)
    : 'Plan';
  const title = hasPaidPlan
    ? `Your ${planName} Plan Has Expired`
    : 'Your Free Trial Has Ended';
  const subtitle = hasPaidPlan
    ? daysExpired > 0
      ? `Your ${normalizedPlan} plan expired ${daysExpired} ${daysExpired === 1 ? 'day' : 'days'} ago.`
      : `Your ${normalizedPlan} plan has expired.`
    : daysExpired > 0
      ? `Your trial expired ${daysExpired} ${daysExpired === 1 ? 'day' : 'days'} ago.`
      : 'Your 15-day free trial period has ended.';
  const description = hasPaidPlan
    ? 'Renew or activate a Silver or Gold plan to continue using all platform features.'
    : 'Activate a Silver or Gold plan to continue using all platform features.';

  const handleContactUs = () => {
    try {
      // Navigate to payment page which will close the overlay due to isPaymentPage check
      // This allows the help panel to be visible
      navigate('/payment');
      // Open help panel after a brief delay
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('openHelp'));
      }, 100);
    } catch (e) {
      console.error('Error in handleContactUs:', e);
    }
  };

  return (
    <div className="trial-expired-overlay">
      <div className="trial-expired-overlay__backdrop" />
      
      <div className="trial-expired-overlay__content">
        <div className="trial-expired-overlay__header">
          <div className="trial-expired-overlay__icon-wrapper">
            <FaExclamationTriangle className="trial-expired-overlay__icon" />
          </div>
          <h1 className="trial-expired-overlay__title">{title}</h1>
          <p className="trial-expired-overlay__subtitle">{subtitle}</p>
          <p className="trial-expired-overlay__description">{description}</p>
        </div>

        <div className="trial-expired-overlay__body">
          <div className="trial-expired-overlay__plans">
            <PlanCard tier="Silver" onContact={handleContactUs} />
            <PlanCard tier="Gold" highlighted onContact={handleContactUs} />
          </div>

          <div className="trial-expired-overlay__features">
            <div className="trial-expired-overlay__feature-item">
              <SilverStar />
              <span><strong>Silver Plan</strong> - Perfect for individuals and small teams</span>
            </div>
            <div className="trial-expired-overlay__feature-item">
              <GoldStar />
              <span><strong>Gold Plan</strong> - Best for growing businesses with advanced needs</span>
            </div>
          </div>
        </div>

        <div className="trial-expired-overlay__footer">
          <div className="trial-expired-overlay__locked-notice">
            <FaLock />
            <span>All features are locked until you activate a plan</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiredOverlay;
